import { UploadIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useProductionDeclarationMaterialAdditionalFilesDialog } from '@/features/production-declaration/hooks/use-production-declaration-material-additional-files-dialog';
import { PURCHASE_ORDER_DOCUMENT_ACCEPT } from '@/features/purchase-order/lib/purchase-order-document-allowed-files';

type ProductionDeclarationMaterialAdditionalFilesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productionDeclarationId: string;
  materialId: string;
  commodityOriginId: string;
  token?: string;
};

export function ProductionDeclarationMaterialAdditionalFilesDialog({
  open,
  onOpenChange,
  productionDeclarationId,
  materialId,
  commodityOriginId,
  token,
}: ProductionDeclarationMaterialAdditionalFilesDialogProps) {
  const {
    dropZoneClassName,
    fileInputRef,
    handleCancel,
    handleDropZoneDragEnter,
    handleDropZoneDragLeave,
    handleDropZoneDragOver,
    handleDropZoneDrop,
    handleFileInputChange,
    handleSubmit,
    isPending,
    openFilePicker,
    selectedFile,
    t,
    tCommon,
  } = useProductionDeclarationMaterialAdditionalFilesDialog({
    commodityOriginId,
    materialId,
    onOpenChange,
    open,
    productionDeclarationId,
    token,
  });

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>
            {t('materialCommodityOrigins.cards.additionalFiles.dialog.title')}
          </DialogTitle>
          <DialogDescription>
            <span className='font-semibold'>
              {t(
                'materialCommodityOrigins.cards.additionalFiles.dialog.descriptionLead',
              )}
            </span>{' '}
            {t(
              'materialCommodityOrigins.cards.additionalFiles.dialog.description',
            )}
          </DialogDescription>
        </DialogHeader>

        <input
          accept={PURCHASE_ORDER_DOCUMENT_ACCEPT}
          className='sr-only'
          onChange={handleFileInputChange}
          ref={fileInputRef}
          type='file'
        />

        <div className='flex flex-col gap-2'>
          <button
            className={dropZoneClassName}
            onClick={openFilePicker}
            onDragEnter={handleDropZoneDragEnter}
            onDragLeave={handleDropZoneDragLeave}
            onDragOver={handleDropZoneDragOver}
            onDrop={handleDropZoneDrop}
            type='button'
          >
            <UploadIcon aria-hidden className='size-10 text-muted-foreground' />
            {selectedFile ? (
              <p className='text-center font-medium text-foreground text-sm'>
                {selectedFile.name}
              </p>
            ) : (
              <>
                <p className='text-center font-semibold text-foreground text-sm'>
                  {t(
                    'materialCommodityOrigins.cards.additionalFiles.dialog.dropzone',
                  )}
                </p>
                <p className='text-center text-muted-foreground text-sm'>
                  {t(
                    'materialCommodityOrigins.cards.additionalFiles.dialog.dropzoneFormats',
                  )}
                </p>
              </>
            )}
          </button>
        </div>

        <DialogFooter>
          <Button onClick={handleCancel} type='button' variant='outline'>
            {tCommon('cancel')}
          </Button>
          <Button
            disabled={isPending || !selectedFile}
            onClick={handleSubmit}
            type='submit'
            variant='declaration-primary'
          >
            {tCommon('add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
